import { IonIcon } from "@ionic/react";
import { chevronForwardOutline } from "ionicons/icons";
import { ReactElement } from "react";
import styled, { css } from "styled-components";
import { cardSurface } from "../../../atoms/Card";
import InlineHtml from "../InlineHtml";
import { resolveRichContentIcon } from "../richContentIcons";
import { safeHref } from "../safeUrl";

interface CardItem {
  title?: string;
  text?: string;
  icon?: string; // curated ionicons name
  iconUrl?: string; // uploaded custom icon — wins over `icon`
  image?: string; // cover image — makes it a media card (image top / body below)
  url?: string; // optional link: the whole card opens it (https/mailto/tel)
}

type CardVariant = "outlined" | "tinted" | "elevated" | "steps";
type CardLayout = "list" | "grid";

interface CardsData {
  items?: CardItem[];
  variant?: CardVariant;
  layout?: CardLayout;
}

/* list = stacked full-width column (default); grid = responsive columns that pack as
   many ~170px tracks as fit (2 on a phone, ~4 in the 720px desktop reading column) —
   the min(100%, …) guard prevents overflow in very narrow containers. */
const Stack = styled.div<{ $grid: boolean }>`
  display: ${({ $grid }) => ($grid ? "grid" : "flex")};
  gap: 12px;

  ${({ $grid }) =>
    $grid
      ? css`
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 170px), 1fr));
          align-items: start;
        `
      : css`
          flex-direction: column;
        `}
`;

/**
 * Card visual variants (block-level, researcher-chosen):
 *  - outlined (default): the app's flat cardSurface look
 *  - tinted: soft accent wash, no border — quiet grouping
 *  - elevated: shadow-lifted card, no border — strong emphasis
 *  - steps: numbered "how it works" sequence — no card chrome, numbered circles
 */
const CardItemBox = styled.div<{ $variant: CardVariant; $linked: boolean; $hasImage: boolean }>`
  position: relative;
  padding: 14px 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border-radius: 16px;

  /* Media card: cover image bleeds to the card edges, body sits below with its own
     padding — so the card becomes a vertical stack and clips the image corners. */
  ${({ $hasImage }) =>
    $hasImage &&
    css`
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      padding: 0;
      overflow: hidden;
    `}

  ${({ $variant }) =>
    $variant === "tinted"
      ? css`
          background: rgba(var(--app-color-accent-rgb, 45, 182, 180), 0.1);
        `
      : $variant === "elevated"
        ? css`
            background: var(--app-color-surface-tint, white);
            box-shadow: 0 4px 14px rgba(var(--app-color-text-rgb, 4, 23, 35), 0.14);
          `
        : $variant === "steps"
          ? css`
              padding: 2px 0;
            `
          : css`
              ${cardSurface}
              background: var(--app-color-surface-tint, white);
            `}

  ${({ $linked }) =>
    $linked &&
    css`
      cursor: pointer;
      transition: box-shadow 0.15s ease, transform 0.15s ease;
      &:hover {
        box-shadow: 0 6px 18px rgba(var(--app-color-text-rgb, 4, 23, 35), 0.16);
      }
      &:active {
        transform: scale(0.995);
      }
    `}
`;

/* Whole-card link (stretched-link pattern): a transparent anchor covering the card,
   so the entire card is clickable, keyboard-focusable and right-click/middle-click
   friendly — without wrapping the card content in an <a> (avoids nested anchors). */
const CardLink = styled.a`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 16px;

  &:focus-visible {
    outline: 2px solid var(--app-color-accent, #2db6b4);
    outline-offset: 2px;
  }
`;

/* Affordance that the card is tappable. Sits below the link overlay (which is
   transparent), so it shows but the link still captures the click. */
const Chevron = styled(IonIcon)`
  flex: none;
  align-self: center;
  font-size: 20px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.4);
`;

/* Cover image of a media card — full width at the top, corners clipped by the card. */
const Cover = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
`;

/* Padded bottom area of a media card (title/text + optional chevron), row-aligned so
   the chevron sits at the right like the icon-card layout. */
const MediaBottom = styled.div`
  padding: 14px 16px;
  display: flex;
  gap: 12px;
  align-items: center;
`;

/* Step number — strong emphasis tone (same family as progress fills). */
const StepCircle = styled.div`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  background: var(--app-color-emphasis, var(--app-color-primary, #2db6b4));
  color: var(--app-color-emphasis-contrast, #ffffff);
`;

const IconCircle = styled.div`
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--app-color-accent-rgb, 45, 182, 180), 0.14);

  ion-icon {
    font-size: 22px;
    color: var(--app-color-accent, #2db6b4);
  }
  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--app-color-text, #041723);
`;

const Text = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  /* Authors write card text in a multiline field — honor their line breaks. */
  white-space: pre-line;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.8);

  a {
    color: var(--app-color-accent, #2db6b4);
    text-decoration: underline;
  }
`;

const CardListBlock = ({ data }: { data: CardsData }): ReactElement | null => {
  // Keep cards with any content — an image-only card (pure visual) is valid.
  const items = (data.items ?? []).filter((item) => item.title || item.text || item.image);
  if (!items.length) return null;
  const variant: CardVariant =
    data.variant === "tinted" || data.variant === "elevated" || data.variant === "steps"
      ? data.variant
      : "outlined";
  const isGrid = data.layout === "grid";
  return (
    <Stack $grid={isGrid}>
      {items.map((item, i) => {
        const hasImage = !!item.image;
        const hasIcon = !hasImage && !!(item.iconUrl || item.icon);
        const href = safeHref(item.url);
        // An image-only card shows just the cover — no empty padded strip below it.
        const hasBottom = !!(item.title || item.text || href);

        // Body + trailing chevron — shared by both layouts (row for icon cards, and
        // the padded bottom row for media cards).
        const body = (
          <>
            <Body>
              {item.title ? <Title>{item.title}</Title> : null}
              {item.text ? (
                <Text>
                  <InlineHtml html={item.text} />
                </Text>
              ) : null}
            </Body>
            {href && <Chevron icon={chevronForwardOutline} aria-hidden="true" />}
          </>
        );

        return (
          <CardItemBox key={i} $variant={variant} $linked={!!href} $hasImage={hasImage}>
            {href && (
              <CardLink
                href={href}
                target={href.startsWith("https://") ? "_blank" : undefined}
                rel={href.startsWith("https://") ? "noopener noreferrer" : undefined}
                aria-label={item.title || item.text || undefined}
              />
            )}
            {hasImage ? (
              <>
                <Cover src={item.image} alt="" />
                {hasBottom && <MediaBottom>{body}</MediaBottom>}
              </>
            ) : (
              <>
                {variant === "steps" ? (
                  <StepCircle aria-hidden="true">{i + 1}</StepCircle>
                ) : hasIcon ? (
                  <IconCircle aria-hidden="true">
                    {item.iconUrl ? (
                      <img src={item.iconUrl} alt="" />
                    ) : (
                      <IonIcon icon={resolveRichContentIcon(item.icon)} />
                    )}
                  </IconCircle>
                ) : null}
                {body}
              </>
            )}
          </CardItemBox>
        );
      })}
    </Stack>
  );
};

export default CardListBlock;
